/**
 * Button - Web port of the mobile design system's AButton
 * (core/libraries/component-library/acomponents/button/AButton.tsx)
 *
 * Mirrors AButton's API: type (hero/standard/link), variant (6), size (3),
 * fullWidth, isLoading, isDisabled, leftIcon/rightIcon. The hero type reproduces
 * the 3D "press-lift" effect using a stacked shadow layer + active:translate-y.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "positive"
  | "negative"
  | "textLink";

export type ButtonSize = "sm" | "md" | "lg";
export type ButtonType = "hero" | "standard" | "link";

interface ButtonProps {
  children?: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Visual style, matches AButton's `type` prop (not the HTML button type attribute) */
  type?: ButtonType;
  /** Native button type attribute, only used when `href` is not set */
  htmlType?: "button" | "submit" | "reset";
  fullWidth?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  /** External link (renders <a>) */
  href?: string;
  /** Internal client-side route (renders react-router Link) */
  to?: string;
  target?: string;
  rel?: string;
  className?: string;
  "aria-label"?: string;
  "data-testid"?: string;
}

const cx = (...classes: Array<string | false | undefined>): string =>
  classes.filter(Boolean).join(" ");

const SHADOW_OFFSET = 4;

// Colors reference the token scale ported into tailwind.config.js.
const VARIANT_CLASSES: Record<ButtonType, Record<ButtonVariant, {
  bg: string;
  hoverActiveBg: string;
  text: string;
  border: string;
  shadow: string;
}>> = {
  hero: {
    primary: { bg: "bg-yellow-500", hoverActiveBg: "hover:bg-yellow-600 active:bg-yellow-600", text: "text-neutral-950", border: "border-yellow-950", shadow: "bg-yellow-950" },
    secondary: { bg: "bg-neutral-700", hoverActiveBg: "hover:bg-neutral-800 active:bg-neutral-800", text: "text-neutral-50", border: "border-neutral-950", shadow: "bg-neutral-950" },
    outline: { bg: "bg-neutral-50", hoverActiveBg: "hover:bg-neutral-100 active:bg-neutral-100", text: "text-neutral-950", border: "border-neutral-950", shadow: "bg-neutral-950" },
    positive: { bg: "bg-green-600", hoverActiveBg: "hover:bg-green-700 active:bg-green-700", text: "text-neutral-50", border: "border-green-950", shadow: "bg-green-950" },
    negative: { bg: "bg-red-600", hoverActiveBg: "hover:bg-red-700 active:bg-red-700", text: "text-neutral-50", border: "border-red-950", shadow: "bg-red-950" },
    textLink: { bg: "bg-transparent", hoverActiveBg: "", text: "text-yellow-600 underline", border: "border-transparent", shadow: "bg-transparent" },
  },
  standard: {
    primary: { bg: "bg-yellow-500", hoverActiveBg: "hover:bg-yellow-700 active:bg-yellow-700", text: "text-neutral-950", border: "border-transparent", shadow: "bg-transparent" },
    secondary: { bg: "bg-neutral-950", hoverActiveBg: "hover:text-yellow-500 active:text-yellow-500", text: "text-neutral-50", border: "border-transparent", shadow: "bg-transparent" },
    outline: { bg: "bg-transparent", hoverActiveBg: "hover:border-neutral-800 hover:text-neutral-800 active:border-neutral-800 active:text-neutral-800", text: "text-neutral-950", border: "border-neutral-950", shadow: "bg-transparent" },
    positive: { bg: "bg-green-600", hoverActiveBg: "hover:bg-green-100 hover:text-green-600 active:bg-green-100 active:text-green-600", text: "text-neutral-50", border: "border-transparent", shadow: "bg-transparent" },
    negative: { bg: "bg-red-600", hoverActiveBg: "hover:bg-red-100 hover:text-red-600 active:bg-red-100 active:text-red-600", text: "text-neutral-50", border: "border-transparent", shadow: "bg-transparent" },
    textLink: { bg: "bg-transparent", hoverActiveBg: "", text: "text-yellow-600 underline", border: "border-transparent", shadow: "bg-transparent" },
  },
  link: {
    primary: { bg: "bg-transparent", hoverActiveBg: "hover:text-yellow-500 active:text-yellow-600", text: "text-neutral-950 underline", border: "border-transparent", shadow: "bg-transparent" },
    secondary: { bg: "bg-transparent", hoverActiveBg: "hover:text-yellow-500 active:text-yellow-600", text: "text-neutral-950 underline", border: "border-transparent", shadow: "bg-transparent" },
    outline: { bg: "bg-transparent", hoverActiveBg: "hover:text-yellow-500 active:text-yellow-600", text: "text-neutral-950 underline", border: "border-transparent", shadow: "bg-transparent" },
    positive: { bg: "bg-transparent", hoverActiveBg: "hover:text-yellow-500 active:text-yellow-600", text: "text-neutral-950 underline", border: "border-transparent", shadow: "bg-transparent" },
    negative: { bg: "bg-transparent", hoverActiveBg: "hover:text-yellow-500 active:text-yellow-600", text: "text-neutral-950 underline", border: "border-transparent", shadow: "bg-transparent" },
    textLink: { bg: "bg-transparent", hoverActiveBg: "hover:text-yellow-500 active:text-yellow-600", text: "text-neutral-950 underline", border: "border-transparent", shadow: "bg-transparent" },
  },
};

const SIZE_CLASSES: Record<ButtonType, Record<ButtonSize, string>> = {
  hero: {
    sm: "px-3 py-2 text-sm gap-2",
    md: "px-3 py-3 text-base gap-2",
    lg: "px-4 py-3 text-lg gap-2",
  },
  standard: {
    sm: "px-2 py-2 text-sm gap-1",
    md: "px-3 py-3 text-base gap-2",
    lg: "px-4 py-4 text-lg gap-2",
  },
  link: {
    sm: "py-2 text-sm gap-1",
    md: "py-3 text-base gap-2",
    lg: "py-4 text-lg gap-2",
  },
};

const RADIUS_CLASSES: Record<ButtonType, Record<ButtonSize, string>> = {
  hero: { sm: "rounded-lg", md: "rounded-lg", lg: "rounded-lg" },
  standard: { sm: "rounded", md: "rounded-lg", lg: "rounded-xl" },
  link: { sm: "rounded", md: "rounded-lg", lg: "rounded-xl" },
};

const ICON_SIZE: Record<ButtonSize, string> = {
  sm: "w-4 h-4",
  md: "w-5 h-5",
  lg: "w-6 h-6",
};

const Button: React.FC<ButtonProps> = ({
  children,
  variant = "primary",
  size = "md",
  type = "hero",
  htmlType = "button",
  fullWidth = false,
  isLoading = false,
  isDisabled = false,
  leftIcon,
  rightIcon,
  onClick,
  href,
  to,
  target,
  rel,
  className,
  ...rest
}) => {
  const disabled = isDisabled || isLoading;
  const colors = VARIANT_CLASSES[type][variant];
  const sizeClasses = SIZE_CLASSES[type][size];
  const radiusClass = RADIUS_CLASSES[type][size];
  const hasBorder = colors.border !== "border-transparent";
  const borderWidthClass = type === "hero" ? "border-2" : "border";
  const hasShadow = type === "hero" && variant !== "textLink";
  const isExternal = target === "_blank";

  const content = (
    <span className="flex items-center justify-center">
      {isLoading ? (
        <Loader2 className={cx(ICON_SIZE[size], "animate-spin")} />
      ) : (
        <>
          {leftIcon}
          {children && <span className={leftIcon || rightIcon ? "mx-2" : undefined}>{children}</span>}
          {rightIcon}
        </>
      )}
    </span>
  );

  const sharedClassName = cx(
    "relative inline-flex items-center justify-center font-medium transition-transform duration-75",
    "focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2",
    "disabled:opacity-50 disabled:pointer-events-none aria-disabled:opacity-50 aria-disabled:pointer-events-none",
    sizeClasses,
    radiusClass,
    colors.bg,
    colors.text,
    colors.border,
    hasBorder && borderWidthClass,
    !disabled && colors.hoverActiveBg,
    hasShadow && !disabled && "active:translate-y-1",
    fullWidth && "w-full"
  );

  // className (spacing/margins) goes on the wrapper, not the button itself - otherwise the
  // button's shrink-to-fit width (and the shadow layer sized against it) would include the margin.
  const wrapperClassName = cx("relative inline-block", fullWidth && "flex w-full", className);

  return (
    <span className={wrapperClassName} style={hasShadow ? { paddingBottom: SHADOW_OFFSET } : undefined}>
      {hasShadow && (
        <span
          aria-hidden="true"
          className={cx("absolute inset-x-0 bottom-0", radiusClass, colors.shadow)}
          style={{ top: SHADOW_OFFSET }}
        />
      )}
      {to ? (
        <Link
          to={disabled ? "#" : to}
          onClick={disabled ? (e) => e.preventDefault() : onClick}
          aria-disabled={disabled || undefined}
          className={sharedClassName}
          {...rest}
        >
          {content}
        </Link>
      ) : href ? (
        <a
          href={href}
          target={target}
          rel={isExternal ? rel ?? "noreferrer noopener" : rel}
          onClick={disabled ? (e) => e.preventDefault() : onClick}
          aria-disabled={disabled || undefined}
          className={sharedClassName}
          {...rest}
        >
          {content}
        </a>
      ) : (
        <button type={htmlType} disabled={disabled} onClick={onClick} className={sharedClassName} {...rest}>
          {content}
        </button>
      )}
    </span>
  );
};

export default Button;
